import { Injectable, Inject, PLATFORM_ID, inject, signal } from '@angular/core';
import { LoggerService } from '@app/core/logging/logger.service';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ThemeService } from './theme.service';
import { BrowserStorageService } from '../../services/browser-storage.service';
import { TenantIdService, TENANT_QA_OVERRIDE_KEY } from './tenant-id.service';
import { TenantWordingService } from './tenant-wording.service';
import { TenantFeatureService } from './tenant-feature.service';
import { TenantAssetService } from './tenant-asset.service';
import { TenantThemeApiResponse, DEFAULT_TENANT_WORDING } from './tenant-theme.interface';

// Cota máxima de espera del endpoint de tema. Sin esto, un http.get que no
// responde (conexión colgada, no error) deja el await pendiente para siempre y
// bloquea la navegación post-login. Al expirar, cae al cache/defaults del catch.
const TENANT_THEME_TIMEOUT_MS = 8000;

const TENANT_LOGO_KEY = 'plp_tenant_logo';
const TENANT_FAVICON_KEY = 'plp_tenant_favicon';

/** Recurso vacío: deja la pestaña sin ícono sin que el navegador salga a pedir
 *  `/favicon.ico`. Mismo valor con el que arranca `index.html`. */
const FAVICON_NEUTRO = 'data:,';
const TENANT_APP_NAME_KEY = 'plp_tenant_app_name';
const TENANT_RESPONSE_CACHE_KEY = 'plp_tenant_response';
/** Ya nadie la ESCRIBE (se retiró la convención `<cdn>/<id>/logo.svg` que la
 *  usaba, ver `main.ts`); se sigue borrando para limpiar el valor que quedó en
 *  los navegadores que ya la tienen. */
const TENANT_CDN_URL_KEY = 'plp_tenant_cdn_url';

@Injectable({ providedIn: 'root' })
export class TenantThemeService {
  /** Registro con nivel por entorno: en producción no escribe en consola. */
  private readonly logger = inject(LoggerService);
  private readonly _dominioNoAsignado = signal(false);

  /**
   * `true` SOLO cuando el tema se resolvió POR HOST y la respuesta dice las DOS
   * cosas: que ningún dominio calzó (`resolvedBy === 'default'`) y que el host
   * pertenece a un nivel que administramos para marcas
   * (`hostGestionado === true`). Ese par es "dominio del nivel que no está
   * asignado a nadie" — el que queda cuando se dio de baja y el navegador
   * todavía lo resuelve por su caché de DNS.
   *
   * ⚠️ **Falla ABIERTO, y es la regla que gobierna todo esto.** Se bloquea solo
   * con `hostGestionado === true` EXPLÍCITO: campo ausente, back anterior,
   * petición fallida, respuesta servida de la caché o la marca blanca apagada
   * por `environment` dejan la señal en `false`. Bloquear la suite por un dato
   * que no llegó sería mucho peor que no bloquearla: el peor caso de fallar
   * abierto es que un subdominio suelto muestre la marca base —lo que pasa
   * hoy—; el de fallar cerrado es dejar la suite inaccesible para todos.
   */
  readonly dominioNoAsignado = this._dominioNoAsignado.asReadonly();

  constructor(
    private http: HttpClient,
    private themeService: ThemeService,
    private browserStorage: BrowserStorageService,
    private tenantIdService: TenantIdService,
    private wordingService: TenantWordingService,
    private featureService: TenantFeatureService,
    private assetService: TenantAssetService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document
  ) {}

  /**
   * Carga y aplica el theme del tenant. Precedencia REAL:
   *
   * 1. `forcedTenantId` → theme POR ID. Es el re-brandeo post-login (el tenant
   *    es propiedad del usuario; el login entrega su `tenant_id`).
   * 2. Tenant AUTORITATIVO (`resolveExplicit()`: sesión > override de QA >
   *    `environment.tenantId`) → theme POR ID.
   * 3. Nada declarado → theme POR HOST (`/tenants/theme/by-host/`), que es la
   *    resolución real pre-login: el back lee el `Origin` del navegador contra
   *    su tabla de dominios. Si contesta que NINGÚN dominio calzó
   *    (`resolvedBy: 'default'`) todavía se prueba la CONVENCIÓN del subdominio,
   *    para no regresionar un host `<slug>.polipay.io` que funcione sin tener
   *    fila en esa tabla.
   *
   * ⚠️ El orden importa y estaba al revés: la convención del subdominio vivía
   * DENTRO de `resolve()`, así que para cualquier host de tres etiquetas —o sea
   * para todo dominio de marca real— se pedía el theme por ese id adivinado y
   * la rama por host era código muerto. Un id que el back no conoce responde
   * `polipay`: eso era el "carga sin branding" de `imony-test.tpx1.polipay.io`.
   */
  async loadTenantTheme(
    forcedTenantId?: string | null,
    opciones?: { refrescar?: boolean },
  ): Promise<void> {
    if (!environment.tenantThemeEnabled) return;
    if (!isPlatformBrowser(this.platformId)) return;

    if (forcedTenantId) {
      this.tenantIdService.override(forcedTenantId);
    }
    const tenantId = forcedTenantId || this.tenantIdService.resolveExplicit();
    let data: TenantThemeApiResponse | null = null;
    const baseUrl = environment.apiUrl;
    // `refrescar` rompe la caché de 5 min (el endpoint responde con
    // Cache-Control: max-age=300): es lo que permite ver el color nuevo AL
    // INSTANTE después de editar la marca, sin recargar la página.
    const bust = opciones?.refrescar ? `?v=${Date.now()}` : '';
    const urlPorId = (id: string) => `${baseUrl}/api/v1/tenants/${id}/theme/${bust}`;

    if (tenantId) {
      data = await this.pedirTema(urlPorId(tenantId));
    } else {
      // Sin tenant declarado se resuelve por el DOMINIO desde el que se abrió
      // la app, que es el único dato disponible antes del login. El back lo lee
      // del `Origin` que pone el navegador; acá no hay que mandar nada.
      //
      // ⚠️ Esta rama NO puede hacer `return`: la caché anti-FOUC de
      // `index.html` no se limpia al cerrar sesión, así que el siguiente
      // usuario de ese navegador veía los colores del anterior pintados y, sin
      // tenant resuelto, el tema NO los corregía. Siempre se resuelve algo (el
      // host, o `polipay` como fallback del propio endpoint).
      data = await this.pedirTema(`${baseUrl}/api/v1/tenants/theme/by-host/${bust}`);
      // ⚠️ El veredicto se toma con lo que contesta el endpoint POR HOST y con
      // NADA más. Evaluarlo sobre la respuesta final dejaba un agujero: bajo un
      // nivel administrado, un host cuya primera etiqueta calza con el slug de
      // una marca viva (`imony.tpx1.polipay.io`) se rescataba solo por la
      // convención del subdominio, así que darlo de baja no cambiaba nada y el
      // dominio seguía sirviendo la marca — justo lo contrario del requisito.
      this.evaluarDominioNoAsignado(data);
      // Y bajo un nivel administrado la TABLA de dominios es la única autoridad:
      // si ella no resolvió, la convención no tiene nada que decir. Además
      // ahorra el viaje.
      if (!this._dominioNoAsignado()) {
        data = await this.reintentarPorConvencion(data, urlPorId);
      }
      // El endpoint por host dice QUÉ marca resolvió: se fija para que el resto
      // de la sesión (assets, comprobantes) no vuelva a resolver por dominio.
      if (data?.tenantId) {
        this.tenantIdService.override(data.tenantId);
      }
    }

    // Un dominio no asignado NO recibe la marca de nadie: ni tokens, ni logo, ni
    // `appName`. Se corta acá, antes de aplicar nada, y se deja el documento en
    // cromo neutro.
    if (this._dominioNoAsignado()) {
      this.aplicarCromoNeutro();
      return;
    }

    if (data) {
      this.cacheResponse(data);
    } else {
      data = this.loadCachedResponse();
    }

    if (!data) {
      this.logger.warn('TenantThemeService: No data from API or cache, using defaults.');
      // Se APLICAN los defaults en vez de dejar lo que hubiera pintado el
      // anti-FOUC: si la caché era de otra marca, no dejarla puesta.
      this.themeService.resetToDefaults();
      return;
    }

    this.applyTokens(data);
    this.applyAssets(data);
    this.applyWording(data);
    // NO se aplican `features` del tenant: el tenant es SOLO rebranding (tokens,
    // logo, wording). Qué módulos ve cada usuario lo decide su acceso (gestores /
    // roles que entrega el login), igual que en dev. Aplicarlos gateaba rutas
    // (p. ej. "Unidades y cuentas") según el seed del back. Los flags del guard y
    // del sidebar se quedan en sus defaults + environment.featureFlags.
  }

  // ── Resolución ─────────────────────────────────────────────

  /** Pide el tema. `null` si la petición falla (el caller cae a la caché). */
  private async pedirTema(url: string): Promise<TenantThemeApiResponse | null> {
    try {
      return await firstValueFrom(
        this.http.get<TenantThemeApiResponse>(url).pipe(timeout(TENANT_THEME_TIMEOUT_MS))
      );
    } catch (error) {
      this.logger.warn('TenantThemeService: API call failed, trying cache.', error);
      return null;
    }
  }

  /**
   * Segunda oportunidad por la CONVENCIÓN del subdominio cuando el back avisa
   * que ningún dominio registrado calzó. Conserva los hosts `<slug>.polipay.io`
   * que hoy funcionan sin fila en la tabla de dominios.
   *
   * ⚠️ El discriminante es `resolvedBy`, que el propio endpoint devuelve, y NO
   * comparar `tenantId` con `'polipay'`: una marca podría llamarse así y, peor,
   * un host DE polipay que sí está registrado resuelve legítimamente por tabla
   * (`resolvedBy: 'host'`) y no habría que reintentarle nada.
   */
  private async reintentarPorConvencion(
    porHost: TenantThemeApiResponse | null,
    urlPorId: (id: string) => string,
  ): Promise<TenantThemeApiResponse | null> {
    if (porHost?.resolvedBy !== 'default') return porHost;

    const candidato = this.tenantIdService.subdomainCandidate();
    if (!candidato || candidato === porHost.tenantId) return porHost;

    const porConvencion = await this.pedirTema(urlPorId(candidato));
    // Un id que el back no conoce responde el fallback (`polipay`), o sea lo
    // mismo que ya se tenía: en ese caso se conserva la respuesta del host.
    const mejora = porConvencion && porConvencion.tenantId !== porHost.tenantId;
    return mejora ? porConvencion : porHost;
  }

  /**
   * Fija `dominioNoAsignado` con la respuesta del endpoint POR HOST. Las dos
   * comparaciones son ESTRICTAS y contra el valor positivo a propósito:
   * `undefined` (back anterior o petición fallida, que llega acá como `null`)
   * cae del lado de NO bloquear.
   */
  private evaluarDominioNoAsignado(porHost: TenantThemeApiResponse | null): void {
    this._dominioNoAsignado.set(
      porHost?.resolvedBy === 'default' && porHost.hostGestionado === true,
    );
  }

  /**
   * Cromo NEUTRO del documento para un dominio que no es de nadie.
   *
   * El documento TAMBIÉN es marca: el título de la pestaña y el favicon son lo
   * primero que se ve, y dejarlos en los de la plataforma sería volver a hacer
   * pasar el dominio por ella — el defecto exacto que este bloqueo corrige. El
   * favicon se NEUTRALIZA en vez de restaurarse: el de la plataforma es su
   * logotipo, no un ícono neutro. El título lo pone el propio aviso, que es
   * quien tiene las traducciones.
   *
   * ⚠️ Se apunta el `<link>` a un recurso VACÍO en vez de QUITARLO, como estaba:
   * sin elemento, el navegador pide `/favicon.ico` por su cuenta, y ese archivo
   * existe en el build (es el que deja el CLI de Angular) — o sea que quitarlo no
   * dejaba la pestaña neutra, le ponía otro ícono.
   *
   * Los tokens vuelven a los defaults —y no se quedan como estén— porque la
   * caché anti-FOUC de `index.html` puede tener pintada la marca de otro; nada
   * en pantalla los consume, pero dejar los de un tercero es peor.
   */
  private aplicarCromoNeutro(): void {
    this.themeService.resetToDefaults();
    this.wordingService.reset();
    this.assetService.reset();
    this.updateFavicon(FAVICON_NEUTRO);
    this.browserStorage.removeLocalStorageItem(TENANT_LOGO_KEY);
    // Si no, el anti-FOUC de `index.html` vuelve a pintar el ícono de la marca
    // cacheada en la próxima carga de este dominio bloqueado.
    this.browserStorage.removeLocalStorageItem(TENANT_FAVICON_KEY);
    // El título lo pone el propio aviso; lo que no puede quedar es el nombre
    // cacheado de una marca, que `index.html` repintaría en la próxima carga.
    this.browserStorage.removeLocalStorageItem(TENANT_APP_NAME_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_RESPONSE_CACHE_KEY);
  }

  // ── Section handlers ───────────────────────────────────────

  private applyTokens(data: TenantThemeApiResponse): void {
    if (data.tokens) {
      this.themeService.applyTheme(data.tokens);
    }
  }

  private applyAssets(data: TenantThemeApiResponse): void {
    if (data.assets) {
      this.assetService.apply(data.assets);
    }
    const faviconUrl = this.assetService.getFaviconUrl();
    if (faviconUrl) {
      this.updateFavicon(faviconUrl);
      // Para el anti-FOUC de `index.html`, que corre antes de Angular: sin esto la
      // pestaña arranca neutra en CADA carga, incluso para quien ya entró antes.
      // ⚠️ La llave ya se borraba en `clear()`, pero nadie la escribía: la caché
      // del favicon estaba a medias.
      this.browserStorage.setLocalStorageItem(TENANT_FAVICON_KEY, faviconUrl);
    } else {
      this.restoreFavicon();
      // Una marca sin favicon no debe dejar cacheado el de la anterior.
      this.browserStorage.removeLocalStorageItem(TENANT_FAVICON_KEY);
    }
    // El logo YA RESUELTO (absoluto) para el splash de `index.html`, que corre
    // antes de Angular y no puede resolver una ruta relativa de la API.
    this.browserStorage.setLocalStorageItem(
      TENANT_LOGO_KEY, this.assetService.getLogoUrl());
  }

  private applyWording(data: TenantThemeApiResponse): void {
    if (data.wording) {
      this.wordingService.apply(data.wording);
    }
    // ⚠️ El título se escribe SIEMPRE, y del wording YA MEZCLADO con los defaults.
    //
    // Antes solo se tocaba si la respuesta traía `appName`, así que una marca sin
    // ese campo —o con `wording: null`— dejaba en la pestaña el nombre de la marca
    // ANTERIOR, o el de la plataforma que `index.html` traía quemado. Es el mismo
    // defecto que ya se corrigió en el favicon: lo que no se pisa, se hereda.
    //
    // El servicio mezcla sobre `DEFAULT_TENANT_WORDING`, así que el respaldo es el
    // nombre de la plataforma sin tener que repetirlo acá.
    const appName = this.wordingService.getSnapshot().appName;
    if (appName) {
      this.document.title = appName;
      // Para el anti-FOUC de `index.html`, que corre antes de Angular: sin esto la
      // pestaña arranca sin nombre en cada carga, incluso para quien ya entró.
      this.browserStorage.setLocalStorageItem(TENANT_APP_NAME_KEY, appName);
    }
  }

  // ── Favicon ────────────────────────────────────────────────

  /** El favicon de la plataforma. Es el que recibe una marca que NO trae el suyo
   *  (y la marca base, cuyo theme declara `faviconUrl: null`). Debe coincidir con
   *  el archivo que el build deja en la raíz. */
  private static readonly FAVICON_PLATAFORMA = '/favicon_polipay.ico?v=2';

  private updateFavicon(url: string): void {
    const existing = this.document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    const link = existing ?? this.document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = url;
    if (!existing) {
      this.document.head.appendChild(link);
    }
  }

  /**
   * Devuelve el favicon de la plataforma. Hace falta porque una marca puede NO
   * traer favicon: sin esto, el ícono de la pestaña se quedaba en el de la marca
   * anterior (todo lo demás sí volvía, porque colores, logo y textos se aplican
   * reemplazando el conjunto completo).
   */
  private restoreFavicon(): void {
    // ⚠️ El destino es una CONSTANTE, ya no "lo que hubiera en el DOM al arrancar".
    //
    // Antes se guardaba el href inicial y se volvía a él. Eso dejó de servir
    // cuando `index.html` pasó a arrancar NEUTRO (`data:,`) para no mostrar la
    // marca de la plataforma mientras se resuelve: el valor capturado sería ese
    // recurso vacío, así que restaurarlo dejaba la pestaña SIN ícono — y con la
    // guarda de "nada que restaurar", con el de la marca ANTERIOR, que es
    // exactamente el defecto que esta función existe para corregir.
    this.updateFavicon(TenantThemeService.FAVICON_PLATAFORMA);
  }

  // ── Response cache ─────────────────────────────────────────

  private cacheResponse(data: TenantThemeApiResponse): void {
    try {
      this.browserStorage.setLocalStorageItem(
        TENANT_RESPONSE_CACHE_KEY,
        JSON.stringify(data)
      );
    } catch (e) {
      this.logger.warn('TenantThemeService: Failed to cache response', e);
    }
  }

  private loadCachedResponse(): TenantThemeApiResponse | null {
    try {
      const cached = this.browserStorage.getLocalStorageItem(TENANT_RESPONSE_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      this.logger.warn('TenantThemeService: Failed to load cached response', e);
      return null;
    }
  }

  // ── Public accessors ───────────────────────────────────────

  /** @deprecated Use TenantAssetService.getLogoUrl() directly. */
  getTenantLogoUrl(): string {
    return this.assetService.getLogoUrl();
  }

  /** @deprecated Use TenantAssetService.getFaviconUrl() directly. */
  getTenantFaviconUrl(): string | null {
    return this.assetService.getFaviconUrl();
  }

  clearTenantTheme(): void {
    this.restoreFavicon();
    this.themeService.resetToDefaults();
    this.wordingService.reset();
    // Igual que el favicon: sin esto la pestaña se queda con el nombre de la marca
    // que se acaba de soltar.
    this.document.title = DEFAULT_TENANT_WORDING.appName;
    this.featureService.reset();
    this.assetService.reset();
    // Clean up localStorage keys
    this.browserStorage.removeLocalStorageItem(TENANT_LOGO_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_FAVICON_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_APP_NAME_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_RESPONSE_CACHE_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_CDN_URL_KEY);
    // El override de QA (?tenant=) vive en sessionStorage: al limpiar el tema se
    // suelta también, para que la siguiente carga vuelva a resolver por sesión/host.
    if (isPlatformBrowser(this.platformId)) {
      try { sessionStorage.removeItem(TENANT_QA_OVERRIDE_KEY); } catch { /* ignore */ }
    }
  }
}
